import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GRANULARITY_OPTIONS } from '../constants';
import type { GranularityValue } from '../constants';
import AnalysisDateRangePicker from './search/AnalysisDateRangePicker';
import AnalysisFilterPopover from './search/AnalysisFilterPopover';
import CountModeToggle from './search/CountModeToggle';
import GroupBySelect from './search/GroupBySelect';
import type { AnalysisFilterBarProps } from './search/types';

/**
 * 分析画面のフィルターバーコンポーネント
 * - 左側: 複合フィルター、グループ選択、人数/件数トグル
 * - 右側: 期間 DatePicker、粒度 Select、カウントモードトグル
 */
export default function AnalysisFilterBar({
  filterOrganizationUserIds,
  organizationUsers,
  filterCompanyTagIds,
  companyTags,
  filterJobTagIds,
  jobTags,
  filterCandidateTagIds,
  candidateTags,
  groupBy,
  metric,
  from,
  to,
  granularity,
  countMode,
  filterTarget,
  onFilterChange,
  onGroupByChange,
  onMetricChange,
  onDateRangeChange,
  onGranularityChange,
  onCountModeChange,
  showMetricToggle = true,
  showCountModeToggle = true,
  availableGroupByOptions,
  availableFilterPanels,
}: AnalysisFilterBarProps) {
  return (
    <div className="flex items-center justify-between gap-3 overflow-x-auto">
      <div className="flex shrink-0 items-center gap-4">
        <AnalysisFilterPopover
          organizationUsers={organizationUsers}
          filterOrganizationUserIds={filterOrganizationUserIds}
          filterTarget={filterTarget}
          companyTags={companyTags}
          filterCompanyTagIds={filterCompanyTagIds}
          jobTags={jobTags}
          filterJobTagIds={filterJobTagIds}
          candidateTags={candidateTags}
          filterCandidateTagIds={filterCandidateTagIds}
          onFilterChange={onFilterChange}
          availablePanels={availableFilterPanels}
        />

        <GroupBySelect
          value={groupBy}
          onChange={onGroupByChange}
          options={availableGroupByOptions}
        />

        {showMetricToggle && (
          <div className="flex h-[42px]">
            {(['uu', 'count'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onMetricChange(m)}
                className={`border border-solid border-border px-2 text-sm transition-colors first:rounded-l-sm last:-ml-px last:rounded-r-sm ${
                  metric === m
                    ? 'bg-primary_lighten font-bold text-primary'
                    : 'bg-white text-sub hover:bg-gray-50'
                }`}
              >
                {m === 'uu' ? '人数' : '件数'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <AnalysisDateRangePicker from={from} to={to} onDateRangeChange={onDateRangeChange} />

        <Select
          value={granularity}
          onValueChange={(v) => onGranularityChange(v as GranularityValue)}
        >
          <SelectTrigger className="h-[42px] w-fit! gap-1 px-3 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GRANULARITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showCountModeToggle && <CountModeToggle value={countMode} onChange={onCountModeChange} />}
      </div>
    </div>
  );
}
